import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import BeatCard from "@/components/BeatCard";
import { LEGENDS } from "@/data/legends";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [myBeats, setMyBeats] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      const { data } = await api.get("/beats?sort=new&limit=200");
      setMyBeats(data.filter((b) => b.user_id === user.id));
    })();
  }, [user]);

  if (!user || typeof user !== "object") return null;

  const onChange = (b) => setMyBeats((prev) => prev.map((x) => (x.id === b.id ? b : x)));

  const pick = async (name) => {
    setSaving(true);
    const ok = await updateProfile({ inspiration: name });
    setSaving(false);
    if (ok) {
      toast.success(name ? `Inspiration set to ${name}` : "Inspiration cleared");
      setPickerOpen(false);
    } else {
      toast.error("Failed to update");
    }
  };

  const currentLegend = LEGENDS.find((l) => l.name === user.inspiration);

  return (
    <div data-testid="profile-page" className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
      <div className="border border-border bg-card p-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono-acc text-xs text-secondary">// PRODUCER</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2">@{user.username}</h1>
          <p className="text-muted-foreground mt-2">{user.email}</p>

          {/* Inspiration badge */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            {currentLegend ? (
              <div
                data-testid="inspiration-badge"
                className="flex items-center gap-3 border border-secondary bg-secondary/5 pr-4 pl-0 py-0"
              >
                <img
                  src={currentLegend.image}
                  alt=""
                  className="w-12 h-12 object-cover grayscale border-r border-secondary"
                />
                <div>
                  <div className="font-mono-acc text-[9px] text-secondary">// INFLUENCED BY</div>
                  <div className="font-display text-lg leading-none mt-1">{currentLegend.name}</div>
                </div>
              </div>
            ) : (
              <div className="font-mono-acc text-[10px] text-muted-foreground border border-dashed border-border px-3 py-2">
                No inspiration pinned yet
              </div>
            )}
            <button
              data-testid="edit-inspiration-btn"
              onClick={() => setPickerOpen(true)}
              className="font-mono-acc text-[10px] border border-border px-3 py-2 hover:border-secondary hover:text-secondary inline-flex items-center gap-2"
            >
              <Sparkles size={12} />
              {currentLegend ? "Change" : "Pick inspiration"}
            </button>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="border border-border p-4 min-w-[100px]">
            <div className="font-mono-acc text-[10px] text-muted-foreground">WINS</div>
            <div className="font-display text-3xl text-secondary">{user.wins || 0}</div>
          </div>
          <div className="border border-border p-4 min-w-[100px]">
            <div className="font-mono-acc text-[10px] text-muted-foreground">VOTES</div>
            <div className="font-display text-3xl text-primary">{user.votes_received || 0}</div>
          </div>
          <div className="border border-border p-4 min-w-[100px]">
            <div className="font-mono-acc text-[10px] text-muted-foreground">BEATS</div>
            <div className="font-display text-3xl">{myBeats.length}</div>
          </div>
        </div>
      </div>

      <h2 className="font-display text-3xl mt-12 mb-6">Your beats</h2>
      {myBeats.length === 0 ? (
        <p className="text-muted-foreground">No beats uploaded yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {myBeats.map((b) => (
            <BeatCard key={b.id} beat={b} onChange={onChange} showCoverGen />
          ))}
        </div>
      )}

      {/* Inspiration picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            data-testid="inspiration-picker"
            className="bg-card border border-border max-w-4xl w-full max-h-[85vh] overflow-auto p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono-acc text-[10px] text-secondary">// PICK YOUR INSPIRATION</div>
                <h2 className="font-display text-3xl mt-2">Who taught you?</h2>
              </div>
              <button
                onClick={() => setPickerOpen(false)}
                data-testid="picker-close"
                className="font-mono-acc text-xs border border-border px-3 py-1 hover:border-primary"
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {LEGENDS.map((l) => {
                const active = user.inspiration === l.name;
                return (
                  <button
                    key={l.name}
                    data-testid={`pick-legend-${l.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    disabled={saving}
                    onClick={() => pick(l.name)}
                    className={`text-left border group overflow-hidden ${
                      active ? "border-secondary" : "border-border hover:border-primary"
                    }`}
                  >
                    <div className="aspect-square overflow-hidden relative">
                      <img
                        src={l.image}
                        alt=""
                        loading="lazy"
                        className={`w-full h-full object-cover transition-all duration-500 ${
                          active ? "grayscale-0" : "grayscale group-hover:grayscale-0"
                        }`}
                      />
                      {active && (
                        <div className="absolute top-2 right-2 w-7 h-7 bg-secondary text-background flex items-center justify-center border border-background">
                          <Check size={14} />
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t border-border">
                      <div className="font-display text-base leading-tight">{l.name}</div>
                      <div className="font-mono-acc text-[9px] text-muted-foreground mt-1">{l.era.split(" ")[0]}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {user.inspiration && (
              <button
                data-testid="pick-clear"
                disabled={saving}
                onClick={() => pick("")}
                className="mt-4 font-mono-acc text-[10px] border border-border px-3 py-2 hover:border-primary hover:text-primary"
              >
                Clear inspiration
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
